import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2 } from "lucide-react";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";
import { useTranslation } from "@/i18n";

export const Route = createFileRoute("/_authenticated/seller/billing")({
  head: () => ({ meta: [{ title: "Fees & Payouts — BidNova Auctions" }] }),
  component: SellerFeesPage,
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
});

function SellerFeesPage() {
  const { t } = useTranslation("settings");

  const included = [
    t("billing.included.noListingFees", "No listing fees"),
    t("billing.included.noMonthlyFees", "No monthly fees"),
    t("billing.included.noSubscriptionFees", "No subscription fees"),
    t("billing.included.commissionOnly", "Commission only on successful sales"),
    t("billing.included.securePayouts", "Secure Stripe payouts in GBP"),
    t("billing.included.buyerProtection", "Buyer protection & dispute handling included"),
  ];

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 sm:py-12 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70">{t("billing.eyebrow", "Seller")}</p>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl">
          <span className="text-gold-gradient">{t("billing.title", "Fees & Payouts")}</span>
        </h1>
        <p className="mt-3 text-base text-muted-foreground max-w-2xl">
          {t("billing.subtitle", "BidNova charges a simple 8% commission on successful sales only. No monthly fees. No subscription fees. No listing fees.")}
        </p>
      </header>

      <Card className="border-gold">
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-3">
              <span>{t("billing.commissionTitle", "Seller commission")}</span>
              <Badge variant="secondary">{t("billing.commissionBadge", "Flat 8%")}</Badge>
            </CardTitle>
            <CardDescription>{t("billing.commissionDesc", "Charged only when your item sells.")}</CardDescription>
          </div>
          <div className="text-4xl font-display text-gold-gradient">8%</div>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          {included.map((f) => (
            <div key={f} className="flex items-start gap-2 text-sm">
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500 shrink-0" />
              <span>{f}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("billing.howTitle", "How payouts are calculated")}</CardTitle>
          <CardDescription>{t("billing.howDesc", "Transparent, itemised on every order.")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="rounded-lg border bg-surface-2 p-4 font-mono text-sm">
            {t("billing.formula", "Seller Payout = Final Sale Price − 8% BidNova Commission")}
          </div>
          <ul className="space-y-2 text-muted-foreground">
            <li>• {t("billing.bullet1", "Buyers pay the full winning bid plus any shipping you set.")}</li>
            <li>• {t("billing.bullet2", "BidNova deducts an 8% commission from the item total (not from shipping).")}</li>
            <li>• {t("billing.bullet3", "The remaining balance is paid out to your connected Stripe account after delivery is confirmed and any dispute window has passed.")}</li>
            <li>• {t("billing.bullet4", "Stripe payment processing fees may apply and are shown on each order.")}</li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("billing.exampleTitle", "Example")}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <table className="w-full">
            <tbody className="divide-y">
              <tr><td className="py-2">{t("billing.exampleFinalPrice", "Final sale price")}</td><td className="py-2 text-right">£100.00</td></tr>
              <tr><td className="py-2">{t("billing.exampleCommission", "BidNova commission (8%)")}</td><td className="py-2 text-right">− £8.00</td></tr>
              <tr className="font-semibold"><td className="py-2">{t("billing.examplePayout", "Seller payout")}</td><td className="py-2 text-right">£92.00</td></tr>
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
